import { FCX } from "@datassential/platform-ui-lib";
import Scrollbar from "react-scrollbars-custom";
import HorizontalBarChart from "@components/HorizontalBarChart";
import { ComponentProps, useMemo } from "react";
import { getMetricColor, Metric } from "@models/Metric";
import cn from "classnames";
import { useConsumerGroupsContext } from "@pages/Profile/components/ConsumerGroups/ConsumerGroups";
import TableSortIcon from "@components/Table/TableSortIcon";
import { useTableSortingColumn, useTableSortingContext } from "@components/Table/TableSorting";

const Chart: FCX<{
    data: ComponentProps<typeof HorizontalBarChart>['data'];
    forceUpdate?: boolean;
    selectedMetricKey?: string;
    enableSorting?: boolean;
}> = ({
    data,
    forceUpdate,
    selectedMetricKey = "baseSize",
    enableSorting = false,
    className,
    testId,
    ...props
}) => {
    const {
        selectedRawNorm,
        selectedCategoryId,
        selectedMetric,
        selectedConsumerGroup,
    } = useConsumerGroupsContext();

    // Get sorting context to include in key
    const { value: sortingValue } = useTableSortingContext();

    const key = useMemo(
        () => {
            if (forceUpdate) {
                return `${selectedConsumerGroup}-${selectedMetric}-${selectedRawNorm}-${selectedCategoryId}-${sortingValue.id}-${sortingValue.direction}`;
            }
            return `${selectedConsumerGroup}-${sortingValue.id}-${sortingValue.direction}`;
        },
        [forceUpdate, selectedConsumerGroup, selectedMetric, selectedRawNorm, selectedCategoryId, sortingValue.id, sortingValue.direction],
    );

    // Call hooks at top level
    const nameSorting = useTableSortingColumn(enableSorting ? "name" : "");
    const metricSorting = useTableSortingColumn(enableSorting ? selectedMetricKey : "");

    // Define sorting configurations with hook results
    const sortingConfigs = useMemo(() => [
        {
            key: "name",
            icon: "Aa",
            testId: "buttonSortingName",
            ...nameSorting
        },
        {
            key: selectedMetricKey,
            icon: "%",
            testId: "buttonSortingMetric",
            ...metricSorting
        }
    ], [selectedMetricKey, nameSorting, metricSorting]);

    return (
        <div
            className={cn(
                "ProfileConsumerGroups__chart-container",
                className,
            )}
            data-testid={testId}
            {...props}
        >
            {/* Chart Sorting Controls - only show when sorting is enabled */}
            {enableSorting && (
                <div className={cn(
                    "ProfileConsumerGroups__chart-sorting",
                    "appears",
                )}>
                    {sortingConfigs.map((config) => (
                        <div
                            key={config.key}
                            className={cn(
                                "ChartHeadSorting",
                                config.isSorted && "is-active",
                            )}
                            onClick={config.onSort}
                            data-testid={config.testId}
                        >
                            <TableSortIcon isActive={config.isSorted} direction={config.direction} />
                            <span className="ProfileConsumerGroups__chart-sorting-icon">{config.icon}</span>
                        </div>
                    ))}
                </div>
            )}
            
            <Scrollbar
                className={cn(enableSorting && "ProfileConsumerGroups__sortable-chart-scrollbar")}
                key={key}
            >
                <HorizontalBarChart
                    className="appears-top"
                    data={data}
                    color={getMetricColor(selectedMetric ?? Metric.NoOpinion)}
                />
            </Scrollbar>
        </div>
    )
};

export default Chart;